/**
 * Money values are CENTS-EXACT in this studio.
 *
 * The amount state used to leak binary floating-point dust: a typed
 * 777.77 could resurface as 777.77001953125 in the Amount field, and
 * a 555.55 entry drifted to 548.5599… after panel switches (the
 * value passed through float arithmetic on its way in and out of
 * inputs, the canvas and stored state). Money must never carry
 * sub-cent dust: every path that sets, parses or displays an amount
 * goes through these two helpers, so the stored value, the field,
 * the check face, the compliance gate and the exports all agree to
 * the cent.
 */

/** Round a numeric amount to cents (half-up at the third decimal). */
export const roundMoney = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Parse an amount from state or user text into a cents-exact
 * number. Accepts numbers and formatted strings ("1,234.56",
 * "$42.5"); anything unparseable or negative becomes 0 (a blank /
 * invalid amount is a blank check, never a negative one).
 */
export const parseMoney = (value: unknown): number => {
    if (typeof value === 'number') return roundMoney(Math.max(0, value));
    if (typeof value === 'string') {
        const n = parseFloat(value.replace(/[^0-9.]/g, ''));
        return Number.isFinite(n) ? roundMoney(Math.max(0, n)) : 0;
    }
    return 0;
};
